/* ================================================================== */
/*  API client for the LifeCraft backend.                              */
/*                                                                     */
/*  Base: https://api.lifecraft4u.com/v1                               */
/*                                                                     */
/*  Auth is httpOnly-cookie based: the server sets `access` (30 min)   */
/*  and `refresh` (30 day) cookies on register/login and reads them on */
/*  protected routes. Bearer tokens are NOT accepted, so every request */
/*  sends `credentials: 'include'` and the frontend never sees the     */
/*  token itself.                                                      */
/* ================================================================== */

export const API_BASE = (
  import.meta.env.VITE_API_BASE ?? 'https://api.lifecraft4u.com/v1'
).replace(/\/$/, '')

export class ApiError extends Error {
  code: number
  constructor(code: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.code = code
  }
}

/**
 * Thin fetch wrapper: always sends cookies, JSON-encodes `json`, and turns a
 * non-2xx response into an ApiError carrying the backend's `message`.
 */
export async function api<T = unknown>(
  path: string,
  opts: Omit<RequestInit, 'body'> & { json?: unknown } = {},
): Promise<T> {
  const { json, headers, ...rest } = opts
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: {
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
    ...rest,
  })

  const text = await res.text()
  const data = text ? JSON.parse(text) : null

  if (!res.ok) {
    const message =
      data && typeof data === 'object' && typeof (data as { message?: unknown }).message === 'string'
        ? (data as { message: string }).message
        : `Request failed (${res.status})`
    throw new ApiError(res.status, message)
  }

  return data as T
}
